'use client';
import Image from 'next/image';
import Link from 'next/link';
import { withMarketQuery } from '@/lib/utils/withMarketQuery';
import { usePathname } from 'next/navigation';
import { useParams } from 'next/navigation';
import { Locales } from '@/types/i18n';
import { useTypedTranslation } from '@/hooks/useTypedTranslation';
import FacebookIcon from '../icons/brands/FacebookIcon';
import XIcon from '../icons/brands/XIcon';
import InstagramIcon from '../icons/brands/InstagramIcon';
import TikTokIcon from '../icons/brands/TikTokIcon';
import styles from './Footer.module.css';

export default function Footer() {
  const { t } = useTypedTranslation();
  const pathname = usePathname();
  const params = useParams();
  const locale = (params.locale as Locales) || 'en';
  const isMerchantsPage = pathname.includes('/merchants');
  const faqsPath = isMerchantsPage ? `/${locale}/merchants/faqs` : `/${locale}/travelers/faqs`;
  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <Image
            src="/images/stamp-tax-free-logo.svg"
            alt="STAMP"
            width={150}
            height={50}
            className={styles.logo}
          />
          <div className={styles.socialLinks}>
            <Link
              href="https://www.facebook.com/askforstamp/"
              aria-label="Facebook"
              className={styles.socialLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={styles.socialIcon}>
                <FacebookIcon fill="currentColor" />
              </span>
            </Link>
            <Link
              href="https://x.com/askforstamp"
              aria-label="Twitter"
              className={styles.socialLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={styles.socialIcon}>
                <XIcon fill="currentColor" />
              </span>
            </Link>
            <Link
              href="https://www.instagram.com/askforstamp?igsh=MTZjdGFvNzlsamlpOA=="
              aria-label="Instagram"
              className={styles.socialLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={styles.socialIcon}>
                <InstagramIcon fill="currentColor" />
              </span>
            </Link>
            <Link
              href="https://www.tiktok.com/@askforstamp?_t=ZN-90VpJfhBx70&_r=1"
              aria-label="TikTok"
              className={styles.socialLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={styles.socialIcon}>
                <TikTokIcon fill="currentColor" />
              </span>
            </Link>
          </div>
        </div>

        <div className={styles.linksSection}>
          <div className={styles.linkColumn}>
            <h3 className={styles.columnTitle}>PRODUCT</h3>
            <ul className={styles.linkList}>
              <li>
                <Link href={withMarketQuery(`/${locale}/travelers`)} className={styles.footerLink}>
                  For Travelers
                </Link>
              </li>
              <li>
                <Link href={withMarketQuery(`/${locale}/merchants`)} className={styles.footerLink}>
                  For Merchants
                </Link>
              </li>
            </ul>
          </div>

          <div className={styles.linkColumn}>
            <h3 className={styles.columnTitle}>GET TO KNOW US</h3>
            <ul className={styles.linkList}>
              <li>
                <a href="https://tally.so/r/wzOPWE" target="_blank" className={styles.footerLink}>
                  Contact Sales
                </a>
              </li>
            </ul>
          </div>

          <div className={styles.linkColumn}>
            <h3 className={styles.columnTitle}>RESOURCES</h3>
            <ul className={styles.linkList}>
              <li>
                <a
                  href="https://blog.stamptaxfree.com/"
                  target="_blank"
                  className={styles.footerLink}
                >
                  Blog
                </a>
              </li>
              <li>
                <a href="https://help.stampit.co/en" target="_blank" className={styles.footerLink}>
                  Help Center
                </a>
              </li>
              <li>
                <Link href={withMarketQuery(faqsPath)} className={styles.footerLink}>
                  FAQs
                </Link>
              </li>
              <li>
                <Link href={withMarketQuery(`/${locale}/sponsors`)} className={styles.footerLink}>
                  {t('navbar.sponsorsAndAwards')}
                </Link>
              </li>
            </ul>
          </div>

          <div className={styles.linkColumn}>
            <h3 className={styles.columnTitle}>LEGAL</h3>
            <ul className={styles.linkList}>
              <li>
                <Link
                  href={withMarketQuery(`/${locale}/privacy-policy`)}
                  className={styles.footerLink}
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="#" className={styles.footerLink}>
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
